import { React, useState, useEffect } from "react";
import ReactDOM from "react-dom";
import "./RoleAttribution.css";
import { useAuth } from "../../../../Security/AuthProvider";
import { useNotification } from '../../../../Utils/Commons/Notification/NotificationContext';

const RoleAttribution = ({ agent, setShowRolePopup }) => {
  const { getCredentials } = useAuth();
  const { notifyError, notifySuccess } = useNotification();
  const [roles, setRoles] = useState([]);
  const [agentRoles, setAgentRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCount, setSelectedCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    fetchRoles();
    fetchAgentRoles();
    // Délai très court pour s'assurer que le CSS est appliqué
    setTimeout(() => setIsVisible(true), 10);
  }, [agent]);

  // Calculer le nombre de rôles sélectionnés
  useEffect(() => {
    setSelectedCount(agentRoles.length);
  }, [agentRoles]);

  const fetchRoles = async () => {
    try {
      const response = await fetch("/roles/get-all", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          company_name: getCredentials().company_name,
          company_password: getCredentials().company_password,
        }),
      });
      const data = await response.json();
      setRoles(data);
    } catch (error) {
      notifyError("Erreur lors de la récupération des rôles");
      console.error("Error fetching roles:", error);
    }
  };

  const fetchAgentRoles = async () => {
    try {
      const response = await fetch("/roles/agent", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          agentId: agent.id,
          company_name: getCredentials().company_name,
          company_password: getCredentials().company_password,
        }),
      });
      const data = await response.json();
      setAgentRoles(Array.isArray(data) ? data : []);
    } catch (error) {
      notifyError("Erreur lors de la récupération des rôles de l'agent");
      console.error("Error fetching agent roles:", error);
      setAgentRoles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleToggle = async (roleId) => {
    const safeAgentRoles = Array.isArray(agentRoles) ? agentRoles : [];
    const isCurrentlyAssigned = safeAgentRoles.some(
      (agentRole) => agentRole.id === roleId
    );

    try {
      const url = isCurrentlyAssigned
        ? "/roles/remove"
        : "/roles/assign";

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          agentId: agent.id,
          roleId: roleId,
          company_name: getCredentials().company_name,
          company_password: getCredentials().company_password,
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        await fetchAgentRoles();
        
        // Gérer le cas où le rôle était déjà assigné
        if (data.alreadyExists) {
          notifySuccess("Qualification déjà attribuée");
        } else {
          notifySuccess(
            isCurrentlyAssigned 
              ? "Qualification retirée avec succès" 
              : "Qualification attribuée avec succès"
          );
        }
      } else {
        console.error("Server error:", data);
        notifyError("Erreur lors de la modification des qualifications");
      }
    } catch (error) {
      console.error("Error toggling role:", error);
      notifyError("Erreur lors de la modification des qualifications");
    }
  };

  if (loading || !isVisible) {
    return ReactDOM.createPortal(
      <div className="role-attribution-overlay">
        <div 
          className="role-attribution-container"
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            margin: 0
          }}
        >
          <div className="role-attribution-content">
            <p>Chargement des qualifications...</p>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return ReactDOM.createPortal(
    <div className="role-attribution-overlay" onClick={() => setShowRolePopup(false)}>
      <div
        className="role-attribution-container"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          margin: 0
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="role-attribution-header">
          <h2 className="role-attribution-title">Gestion des Qualifications</h2>
          <p className="role-attribution-subtitle">
            Agent : {agent?.name}
          </p>
        </div>
        
        <div className="role-attribution-content">
          <div className="role-attribution-info">
            <p>
              Sélectionnez les qualifications que possède cet agent. 
              Ces qualifications détermineront les types de missions qu'il pourra effectuer.
            </p>
          </div>

          {roles.length === 0 ? (
            <div className="no-roles-message">
              <p>Aucune qualification disponible</p>
              <p>Créez d'abord des qualifications dans les paramètres</p>
            </div>
          ) : (
            <div className="roles-grid">
              {roles.map((role) => {
                const safeAgentRoles = Array.isArray(agentRoles) ? agentRoles : [];
                const isSelected = safeAgentRoles.some(
                  (agentRole) => agentRole.id === role.id
                );
                
                return (
                  <div 
                    key={role.id} 
                    className={`role-item ${isSelected ? 'selected' : ''}`}
                  >
                    <div className="role-badge">Assigné</div>
                    <label className="role-checkbox">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleRoleToggle(role.id)}
                      />
                      <span className="role-name">{role.name}</span>
                    </label>
                  </div>
                );
              })}
            </div>
          )}

          <div className="role-attribution-actions">
            <div className="role-count-info">
              <span className="count">{selectedCount}</span> qualification(s) assignée(s)
            </div>
            <button
              className="role-attribution-button"
              onClick={() => setShowRolePopup(false)}
            >
              Terminer
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RoleAttribution;
